/**
 * Live swipe-drag channel between the gesture layer and the table carousel.
 *
 * The touch handlers live in SlideContainer (above the table view) while the
 * track that moves lives in RebindingTableView (inside Game). Threading a live
 * drag offset through Game as props would re-render the whole table subtree on
 * every touchmove, so it travels through this module instead — and in two
 * channels, deliberately:
 *
 *   • PHASE changes (drag started / released / finished) are rare and drive
 *     React state: which tables need to be on screen.
 *   • OFFSET updates fire on every touchmove and drive nothing but a
 *     `transform` written straight to the DOM. No React work per frame is what
 *     keeps the drag stuck to the finger.
 */

export type DragSession = {
  /** Table to the LEFT in add-order, if any (revealed by dragging right). */
  prev: string | null;
  /** The table being dragged. */
  cur: string;
  /** Table to the RIGHT in add-order, if any (revealed by dragging left). */
  next: string | null;
};

export type DragPhase =
  | { phase: 'idle' }
  | { phase: 'dragging'; session: DragSession }
  /**
   * Released: settle to `target` (a neighbour) or back to `cur` when null.
   * `velocityPxPerMs` is the release speed — the settle uses it to size its
   * own duration so the animation continues the gesture rather than replacing
   * it with a fixed-length slide.
   */
  | {
      phase: 'settling';
      session: DragSession;
      target: string | null;
      velocityPxPerMs: number;
    };

type PhaseListener = (state: DragPhase) => void;
type OffsetListener = (dx: number) => void;

let state: DragPhase = { phase: 'idle' };
let offset = 0;
const phaseListeners = new Set<PhaseListener>();
const offsetListeners = new Set<OffsetListener>();

/**
 * Stillness watchdog. A drag whose end event never arrives (an OS gesture or
 * app switch that swallows the touchcancel, a handler that threw mid-commit)
 * strands the phase at 'dragging' - and everything gated on the switch lock
 * (the fixed chrome, new gestures) stays dead until the 60s drag deadline
 * expires on its own; observed on device as a ~minute of locked UI that
 * "fixed itself". No real horizontal drag holds the felt perfectly still this
 * long: if no movement arrives for this window, treat the gesture as abandoned
 * and spring back.
 */
const DRAG_STILLNESS_TIMEOUT_MS = 10_000;
let stillnessTimer: ReturnType<typeof setTimeout> | null = null;
let lastDragActivityAt = 0;

const disarmStillnessWatchdog = (): void => {
  if (stillnessTimer !== null) clearTimeout(stillnessTimer);
  stillnessTimer = null;
};

const armStillnessWatchdog = (delayMs: number): void => {
  disarmStillnessWatchdog();
  stillnessTimer = setTimeout(() => {
    stillnessTimer = null;
    if (state.phase !== 'dragging') return;
    const idleFor = Date.now() - lastDragActivityAt;
    if (idleFor >= DRAG_STILLNESS_TIMEOUT_MS) {
      endSwipeDrag(null); // abandoned - spring back, release the switch lock
    } else {
      armStillnessWatchdog(DRAG_STILLNESS_TIMEOUT_MS - idleFor);
    }
  }, delayMs);
};

const emitPhase = () => phaseListeners.forEach((l) => l(state));

export const getDragState = (): DragPhase => state;
export const getDragOffset = (): number => offset;

/**
 * Publish an offset to every listener, whatever the phase.
 *
 * The offset channel is the ONE place that knows where the carousel is, and
 * everything that should move with the felt — the track, the indicator dot —
 * reads it. It used to fall silent the instant the finger lifted, because only
 * `updateSwipeDrag` published and that is guarded on the dragging phase. The
 * track carried on moving (its own keyframe animation) while every other
 * subscriber froze at the last dragged value: exactly why the dot stuck to one
 * side after a swipe that sprang back. The settle spring publishes through here
 * too now, so followers stay attached from first touch to final rest.
 */
export const publishDragOffset = (dx: number): void => {
  offset = dx;
  offsetListeners.forEach((l) => l(dx));
};

export const subscribeDragPhase = (listener: PhaseListener): (() => void) => {
  phaseListeners.add(listener);
  return () => phaseListeners.delete(listener);
};

export const subscribeDragOffset = (listener: OffsetListener): (() => void) => {
  offsetListeners.add(listener);
  return () => offsetListeners.delete(listener);
};

/**
 * Finger has committed to a horizontal drag: put the neighbours on screen.
 *
 * Returns the offset the new gesture must ADD its travel to. That is 0 for a
 * drag starting from rest, but grabbing a settle already in flight returns
 * wherever the spring had got to, so the track stays exactly under the finger
 * instead of jumping back to a panel boundary. Interrupting a settle keeps the
 * panels that settle was already showing — the live transform is expressed
 * relative to them, so swapping the session mid-flight would teleport the felt.
 */
export const beginSwipeDrag = (session: DragSession): number => {
  if (state.phase === 'settling') {
    if (state.target === null) {
      // Spring-back: nothing about the view changed, so the OLD session and
      // the live offset both still mean what they meant. (Original takeover.)
      state = { phase: 'dragging', session: state.session };
    } else {
      // COMMITTED settle (EN-10545, "constantly responsive"): the app's
      // current table is already the destination — openTable fired at commit —
      // so the caller's freshly-built session (around that new table) is the
      // right frame. The live offset is still expressed against the OLD rest;
      // the new rest sits exactly `settleTargetPx` away from it, so shifting
      // by that much re-bases the position without moving a pixel on screen:
      // the felt stays exactly where the spring had carried it, and the new
      // gesture continues from there. This was previously refused outright —
      // see the old isSpringingBack note — which made a quick second swipe
      // (most naturally, a reversal) land in a dead window of ~half a second.
      offset -= settleTargetPx;
      state = { phase: 'dragging', session };
    }
    // The takeover is still drag activity — the watchdog (EN-10538) must not
    // fire mid-gesture just because the takeover skipped the cold-start path.
    lastDragActivityAt = Date.now();
    armStillnessWatchdog(DRAG_STILLNESS_TIMEOUT_MS);
    emitPhase();
    return offset;
  }
  state = { phase: 'dragging', session };
  offset = 0;
  lastDragActivityAt = Date.now();
  armStillnessWatchdog(DRAG_STILLNESS_TIMEOUT_MS);
  emitPhase();
  return offset;
};

/**
 * Where the running settle is heading, in offset px (0 for a spring-back).
 * Reported by the carousel when it starts the settle — it is the one place
 * that knows the panel pitch — and consumed by beginSwipeDrag's re-base.
 */
let settleTargetPx = 0;
export const noteSettleTargetPx = (px: number): void => {
  settleTargetPx = px;
};

/** True while ANY settle is running — grabbable either way now. */
export const isSwipeSettling = (): boolean => state.phase === 'settling';

/** Every touchmove. Offset in px: negative = dragging toward the next table. */
export const updateSwipeDrag = (dx: number): void => {
  if (state.phase !== 'dragging') return;
  // Stamp only - the armed watchdog re-derives its own deadline from this, so
  // a 120Hz move stream costs no timer churn.
  lastDragActivityAt = Date.now();
  publishDragOffset(dx);
};

/**
 * Finger lifted (or the trackpad gesture stopped). `target` is the table to
 * settle onto — a neighbour when the drag passed the commit threshold, or null
 * to spring back to the current one.
 *
 * `velocityPxPerMs` is the speed at RELEASE, carried through so the settle can
 * continue the gesture instead of overriding it: a flick finishes fast, a slow
 * let-go eases. Defaults to 0, which yields the neutral duration — every
 * existing caller that doesn't measure velocity behaves exactly as before.
 */
export const endSwipeDrag = (target: string | null, velocityPxPerMs = 0): void => {
  if (state.phase !== 'dragging') return;
  disarmStillnessWatchdog();
  state = { phase: 'settling', session: state.session, target, velocityPxPerMs };
  emitPhase();
};

/**
 * The settle finished (or was abandoned) — back to a resting view.
 *
 * Publishes a final 0 so followers return to their resting position under their
 * own styling. By this point React has already re-pointed the selection at the
 * table that was settled onto, so "offset 0" and "where the spring left the
 * felt" describe the same place on screen: the dot lands where it was already
 * standing rather than jumping.
 */
export const clearSwipeDrag = (): void => {
  const wasActive = state.phase !== 'idle';
  disarmStillnessWatchdog();
  state = { phase: 'idle' };
  // Reset the offset even when the phase was ALREADY idle. A programmatic
  // switch (tapping a table button) never enters the dragging phase at all, so
  // guarding this on the phase left the offset parked at one panel width after
  // every tap — and the next switch's spring then started from there and drove
  // the felt the wrong way. Keyed on the offset itself, so the common case of
  // this being called at rest costs nothing.
  if (offset !== 0) publishDragOffset(0);
  if (wasActive) emitPhase();
};

/** True while a finger-driven swipe owns the table surface. */
export const isSwipeDragActive = (): boolean => state.phase !== 'idle';

/**
 * True while a FAILED gesture is springing back to the table it started on.
 *
 * Such a settle can be grabbed and taken over, because nothing about the view
 * has changed: the same table is current, the same panels are on screen, and
 * the offset the spring is animating means the same thing to a new gesture as
 * it did to the old one. Picking the felt back up mid-return is a natural thing
 * to do — you under-swiped, saw it coming back, and pushed again — and it used
 * to be swallowed by the switch lock.
 *
 * A COMMITTED settle deliberately does not qualify. The table change is
 * published the instant the gesture commits, so by the time that spring is
 * running the app's current table is already the destination while the panels
 * on screen still belong to the departing session. A second gesture would
 * resolve its neighbours against the new table and its offset against the old
 * panels, and commit to a table that is not on screen. Letting that settle
 * finish costs a few hundred ms of a gesture nobody makes mid-flight; getting
 * it wrong puts the player on the wrong felt.
 */
export const isSpringingBack = (): boolean => state.phase === 'settling' && state.target === null;
